import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Bot, Send, Loader2, Plus, MessageSquare, Trash2, FileText, X, Paperclip, Search, Menu, ThumbsUp, ThumbsDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNotification } from '../context/NotificationContext';

// Preset queries for beginners / not logged in
const PRESET_QUERIES = [
  'Show shift-wise unplanned downtime for last 30 days.',
  'What is the top 10 issued components for the fleet?',
  'Compare TK35 and TK17 for SMU, downtime, and unplanned event count.',
  'Show me the downtime analysis for TK05 in last three months.',
  'How does hot weather affect the truck fleet health?'
];

// Fallback suggestions when logged-in user has no chat history yet
const LOGGED_IN_FALLBACK_QUERIES = [
  'Show shift-wise unplanned downtime for last 30 days.',
  'What is the top 10 issued components for the fleet?',
  'Compare TK35 and TK17 for SMU, downtime, and unplanned event count.',
  'Show me the downtime analysis for TK05 in last three months.',
  'How does hot weather affect the truck fleet health?'
];

const MAX_USER_FAQ_SUGGESTIONS = 5;
const STANDARD_DISCLAIMER =
  'note: ai can make mistakes. please use the analysis cautiously and verify important information.';
const DISCLAIMER_PATTERNS = [
  /\n*\s*\*{0,2}note\*{0,2}\s*:\s*ai can make mistakes\.[\s\S]*$/i,
  /\n*\s*ai can make mistakes\.[\s\S]*$/i,
];
const HEADING_LINE_RE = /^[A-Z][A-Za-z0-9 /&()-]{2,44}:?$/;
const BAR_LINE_RE = /^([A-Za-z0-9][A-Za-z0-9.,% /&()-]{1,60}):\s*([#.=]{5,})\s*\((\d+(?:\.\d+)?)%\)\s*$/;
const BAR_BOLD_LINE_RE = /^\*\*([A-Za-z0-9][A-Za-z0-9.,% /&()-]{1,60})\s*:\*\*\s*([#.=]{5,})\s*\((\d+(?:\.\d+)?)%\)\s*$/;

function flattenText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(flattenText).join('');
  if (React.isValidElement(value)) return flattenText(value.props?.children);
  return '';
}

function parseComparisonBarChildren(children) {
  // First pass: wrapper-agnostic parse from flattened text.
  // ReactMarkdown frequently emits <li><p>...</p></li>, so relying on direct
  // strong/code children is not stable across markdown shapes.
  const flatRaw = flattenText(children).replace(/\s+/g, ' ').trim();
  const flatNoBullet = flatRaw.replace(/^[\u2022-]\s*/, '').trim();
  const flatNormalized = flatNoBullet.replace(/\*\*/g, '').replace(/`/g, '').trim();
  const flatMatch = flatNormalized.match(/^([A-Za-z0-9][A-Za-z0-9.,% /&()-]{1,60}):\s*([#.=]{5,})\s*\((\d+(?:\.\d+)?)%\)$/);
  if (flatMatch) {
    const [, label, bar, pct] = flatMatch;
    const pctValue = Math.max(0, Math.min(100, Number(pct)));
    if (Number.isFinite(pctValue)) {
      return { label: label.trim(), bar, pctValue };
    }
  }

  // Second pass: structured parse when strong/code are direct children.
  const parts = React.Children.toArray(children);
  let label = '';
  let bar = '';
  let pct = '';
  let trailingText = '';

  for (const part of parts) {
    if (typeof part === 'string' || typeof part === 'number') {
      trailingText += String(part);
      continue;
    }
    if (!React.isValidElement(part)) continue;
    const elementType = typeof part.type === 'string' ? part.type.toLowerCase() : '';
    const text = flattenText(part.props?.children).trim();
    if (!text) continue;

    if (elementType === 'strong') {
      label = text.replace(/:\s*$/, '').trim();
      continue;
    }
    if (elementType === 'code') {
      bar = text;
      continue;
    }
    trailingText += text;
  }

  const pctMatch = trailingText.match(/\((\d+(?:\.\d+)?)%\)/);
  if (pctMatch) pct = pctMatch[1];

  if (!label || !bar || !pct || !/^[#.=]{5,}$/.test(bar)) return null;
  const pctValue = Math.max(0, Math.min(100, Number(pct)));
  if (!Number.isFinite(pctValue)) return null;

  return { label, bar, pctValue };
}

function parseAsciiBarToken(value) {
  const token = String(value || '').trim();
  if (!/^[#.=]{5,}$/.test(token)) return null;
  const chars = token.split('');
  const filled = chars.filter((c) => c === '#' || c === '=').length;
  const pctValue = chars.length > 0 ? (filled / chars.length) * 100 : 0;
  return {
    bar: token,
    pctValue: Math.max(0, Math.min(100, pctValue)),
  };
}

/** Normalize a user query for grouping (trim, collapse spaces). */
function normalizeQuery(text) {
  return (text || '').trim().replace(/\s+/g, ' ');
}

function normalizeAssistantTextForDedupe(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  let core = raw;
  for (const pattern of DISCLAIMER_PATTERNS) {
    core = core.replace(pattern, '').trim();
  }
  // Backward-compatible fallback for any unusual disclaimer variants.
  const lower = core.toLowerCase();
  const idx = lower.indexOf(STANDARD_DISCLAIMER);
  const finalCore = idx >= 0 ? core.slice(0, idx).trim() : core;
  return finalCore.replace(/\s+/g, ' ').trim().toLowerCase();
}

function stripAssistantDisclaimer(text) {
  let cleaned = String(text || '');
  for (const pattern of DISCLAIMER_PATTERNS) {
    cleaned = cleaned.replace(pattern, '').trim();
  }
  return cleaned;
}

function normalizeMarkdownForDisplay(text) {
  const input = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!input) return input;

  const lines = input.split('\n');
  const normalized = [];

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i];
    const line = raw.trim();
    const next = (lines[i + 1] || '').trim();

    // Promote standalone bold labels to headings.
    const boldLabel = line.match(/^\*\*([A-Za-z][A-Za-z0-9 /&()-]{2,44})\s*:\*\*$/);
    if (boldLabel) {
      normalized.push(`### ${boldLabel[1].trim()}`);
      continue;
    }

    // Promote plain section labels (with or without trailing colon) to headings.
    if (
      HEADING_LINE_RE.test(line) &&
      !line.startsWith('#') &&
      !line.startsWith('- ') &&
      !line.startsWith('* ') &&
      !line.startsWith('|') &&
      next !== ''
    ) {
      normalized.push(`### ${line.replace(/:\s*$/, '').trim()}`);
      continue;
    }

    // Normalize comparison bar lines to markdown bullets with inline code.
    // Accept plain ("TK35 events: ####"), bold ("**TK35 events:** ####"), and pre-bulleted forms.
    const lineNoBullet = line.replace(/^-\s+/, '').trim();
    const barMatch = lineNoBullet.match(BAR_BOLD_LINE_RE) || lineNoBullet.match(BAR_LINE_RE);
    if (barMatch) {
      const [, label, bar, pct] = barMatch;
      normalized.push(`- **${label.trim()}:** \`${bar}\` (${pct}%)`);
      continue;
    }

    normalized.push(raw);
  }

  return normalized
    .join('\n')
    .replace(/([^\n])\n(###\s+)/g, '$1\n\n$2')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function isAssistantMessage(msg) {
  const t = String(msg?.type || '').toLowerCase();
  return t === 'ai' || t === 'assistant' || t === 'bot';
}

const getChatMarkdownComponents = (isCompact = false) => ({
  h1: ({ node, children, ...props }) => <h1 className={`${isCompact ? 'text-[1.35rem] mt-1.5 mb-3' : 'text-[1.55rem] mt-2 mb-4'} font-bold tracking-tight`} {...props}>{children}</h1>,
  h2: ({ node, children, ...props }) => <h2 className={`${isCompact ? 'text-[1.18rem] mt-1.5 mb-2.5' : 'text-[1.35rem] mt-2 mb-3'} font-bold tracking-tight`} {...props}>{children}</h2>,
  h3: ({ node, children, ...props }) => <h3 className={`${isCompact ? 'text-[1.02rem] mt-3.5 mb-1.5' : 'text-[1.15rem] mt-5 mb-2'} font-semibold`} {...props}>{children}</h3>,
  h4: ({ node, children, ...props }) => <h4 className={`${isCompact ? 'text-[0.95rem] mt-3 mb-1.5' : 'text-[1rem] mt-4 mb-2'} font-semibold`} {...props}>{children}</h4>,
  p: ({ node, children, ...props }) => <p className={`${isCompact ? 'my-1.5 text-[14px] leading-6' : 'my-2 text-[15px] leading-7'}`} {...props}>{children}</p>,
  ul: ({ node, children, ...props }) => <ul className={`${isCompact ? 'my-1.5 pl-5 space-y-0.5' : 'my-2 pl-6 space-y-1'} list-disc`} {...props}>{children}</ul>,
  ol: ({ node, children, ...props }) => <ol className={`${isCompact ? 'my-1.5 pl-5 space-y-0.5' : 'my-2 pl-6 space-y-1'} list-decimal`} {...props}>{children}</ol>,
  li: ({ node, children, ...props }) => {
    const parsedBar = parseComparisonBarChildren(children);
    if (parsedBar) {
      return (
        <li className={`${isCompact ? 'text-[14px] leading-6' : 'text-[15px] leading-7'}`} {...props}>
          <div className="comparison-bar-line">
            <span className="comparison-bar-label">{parsedBar.label}:</span>
            <span className={`comparison-bar-track ${isCompact ? 'comparison-bar-track-compact' : ''}`}>
              <span className="comparison-bar-fill" style={{ width: `${parsedBar.pctValue}%` }} />
              <span className="comparison-bar-text-ghost" aria-hidden="true" />
            </span>
            <span className="comparison-bar-pct">({parsedBar.pctValue.toFixed(1)}%)</span>
          </div>
        </li>
      );
    }
    return <li className={`${isCompact ? 'text-[14px] leading-6' : 'text-[15px] leading-7'}`} {...props}>{children}</li>;
  },
  hr: ({ node, ...props }) => <hr className={`${isCompact ? 'my-4' : 'my-5'} border-0 border-t border-gray-400`} {...props} />,
  blockquote: ({ node, children, ...props }) => (
    <blockquote className={`${isCompact ? 'my-2 py-1.5 pl-2.5 pr-2' : 'my-3 py-2 pl-3 pr-2'} border-l-4 border-gray-300 bg-gray-50/80`} {...props}>{children}</blockquote>
  ),
  table: ({ node, children, ...props }) => (
    <div className={`chat-markdown-table-wrap ${isCompact ? 'my-2 rounded-md' : 'my-3 rounded-lg'} overflow-x-auto border border-gray-300`}>
      <table className={`${isCompact ? 'text-[13px]' : 'text-sm'} w-full border-collapse`} {...props}>{children}</table>
    </div>
  ),
  thead: ({ node, children, ...props }) => <thead className="bg-gray-50" {...props}>{children}</thead>,
  tbody: ({ node, children, ...props }) => <tbody {...props}>{children}</tbody>,
  tr: ({ node, children, ...props }) => <tr className="border-b border-gray-300 last:border-b-0" {...props}>{children}</tr>,
  th: ({ node, children, ...props }) => <th className={`${isCompact ? 'px-2.5 py-1.5' : 'px-3 py-2'} border-r border-gray-300 text-left font-semibold last:border-r-0`} {...props}>{children}</th>,
  td: ({ node, children, ...props }) => <td className={`${isCompact ? 'px-2.5 py-1.5' : 'px-3 py-2'} border-r border-gray-300 align-top last:border-r-0`} {...props}>{children}</td>,
  pre: ({ node, children, ...props }) => <pre className={`${isCompact ? 'my-2 rounded-md p-2.5 text-[12px]' : 'my-3 rounded-lg p-3'} overflow-x-auto bg-gray-900 text-gray-100`} {...props}>{children}</pre>,
  code: ({ inline, className, children, ...props }) => (
    inline ? (() => {
      const raw = flattenText(children);
      const asciiBar = parseAsciiBarToken(raw);
      if (asciiBar) {
        return (
          <span
            className={`comparison-bar-track ${isCompact ? 'comparison-bar-track-compact' : ''}`}
            aria-label={`comparison bar ${asciiBar.pctValue.toFixed(1)} percent`}
            {...props}
          >
            <span className="comparison-bar-fill" style={{ width: `${asciiBar.pctValue}%` }} />
            <span className="comparison-bar-text-ghost" aria-hidden="true" />
          </span>
        );
      }
      return <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.9em]" {...props}>{children}</code>;
    })() : (
      <code className={className} {...props}>{children}</code>
    )
  ),
});

const ChatManager = ({
  loading,
  chatsLoading,
  processingStatus,
  query,
  setQuery,
  handleSubmit,
  handleKeyPress,
  chats,
  activeChatId,
  createNewChat,
  switchChat,
  deleteChat,
  clearConversation,
  handleFileUpload,
  cancelRequest,
  apiClient,
  isSidebarMode = false,
  isAuthenticated = false
}) => {
  const { notify } = useNotification();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSidebar, setShowMobileSidebar] = useState(false); // Mobile sidebar toggle
  const [showHistoryMenu, setShowHistoryMenu] = useState(false); // History dropdown menu

  // State to track feedback given by user: { messageId: 'up' | 'down' }
  const [messageFeedback, setMessageFeedback] = useState({});
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [activeFeedbackMsgId, setActiveFeedbackMsgId] = useState(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // --- Random Feedback Logic ---
  const [showRandomFeedbackPrompt, setShowRandomFeedbackPrompt] = useState(false);

  // Track usage for random feedback prompts
  const [usageCount, setUsageCount] = useState(() => {
    const saved = localStorage.getItem('chat_usage_count');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [lastFeedbackPrompt, setLastFeedbackPrompt] = useState(() => {
    const saved = localStorage.getItem('chat_last_feedback_prompt');
    return saved ? parseInt(saved, 10) : 0;
  });

  // Random feedback prompt logic
  const shouldShowFeedbackPrompt = React.useCallback(() => {
    const now = Date.now();
    const timeSinceLastPrompt = now - lastFeedbackPrompt;
    const thirtyMinutes = 30 * 60 * 1000;

    if (timeSinceLastPrompt < thirtyMinutes) {
      return false;
    }

    // Show prompt based on usage patterns
    const sessionCount = usageCount % 100;
    if (sessionCount === 2 && Math.random() < 0.6) return true;
    if (sessionCount === 3 && Math.random() < 0.85) return true;
    if (sessionCount >= 5 && sessionCount % 3 === 0) return true;

    return false;
  }, [lastFeedbackPrompt, usageCount]);


  const handleFeedback = async (messageId, isPositive) => {
    // Update local state immediately
    const feedbackType = isPositive ? 'up' : 'down';

    // Toggle off if clicking same button
    if (messageFeedback[messageId] === feedbackType) {
      setMessageFeedback(prev => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });
      try {
        await apiClient.post(`/feedback`, {
          chat_id: activeChatId,
          message_id: messageId,
          is_positive: null,
          feedback_text: ''
        });
      } catch (error) {
        console.error('Failed to remove feedback:', error);
      }
    } else {
      setMessageFeedback(prev => ({ ...prev, [messageId]: feedbackType }));

      // Send to backend
      try {
        await apiClient.post(`/feedback`, {
          chat_id: activeChatId,
          message_id: messageId,
          is_positive: isPositive,
          feedback_text: ''
        });
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    }
  };

  const handleCommentSubmit = async () => {
    if (!activeFeedbackMsgId || !commentText.trim()) return;

    setIsSubmittingFeedback(true);
    try {
      // Determine if it was already up or down, or neutral
      const currentFeedback = messageFeedback[activeFeedbackMsgId];
      const isPositive = currentFeedback === 'up' || currentFeedback === undefined; // Default to true if not specified

      await apiClient.post(`/feedback`, {
        chat_id: activeChatId,
        message_id: activeFeedbackMsgId,
        is_positive: isPositive,
        feedback_text: commentText
      });

      setShowCommentModal(false);
      setCommentText('');
      setActiveFeedbackMsgId(null);
    } catch (error) {
      console.error('Failed to submit comment:', error);
      notify({ type: 'error', title: 'Comment failed', message: 'Failed to submit comment. Please try again.' });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Removed unused handleDownloadExcel function
  // const handleDownloadExcel = async (metadata) => {
  //   if (!metadata) return;
  //   try {
  //     const toolName = metadata.handler_type || (metadata.intent === 'pre_failure' ? 'pre_failure' : 'event_count');
  //     const filename = `${toolName}_report_${new Date().toISOString().split('T')[0]}.xlsx`;
  //     const response = await apiClient.post(`/analysis/${toolName}/export`, metadata, {
  //       responseType: 'blob'
  //     });
  //     const url = window.URL.createObjectURL(new Blob([response.data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', filename);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch (error) {
  //     console.error('Export failed:', error);
  //     alert('Failed to download Excel report. Please try again.');
  //   }
  // };

  // Removed unused memory warning feature
  // const CHAT_MEMORY_THRESHOLD = 50;
  // useEffect(() => {
  //   if (chats.length > CHAT_MEMORY_THRESHOLD) {
  //     setShowMemoryWarning(true);
  //   } else {
  //     setShowMemoryWarning(false);
  //   }
  // }, [chats.length]);

  // Helper function to highlight search matches in text
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;

    // Escape special regex characters
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 text-yellow-900">{part}</mark>
      ) : (
        part
      )
    );
  };

  // Filter chats based on search query
  const filteredChats = useMemo(() => {
    if (!searchQuery.trim()) {
      return chats;
    }

    const query = searchQuery.toLowerCase();
    return chats.filter(chat => {
      // Search in title
      if (chat.title.toLowerCase().includes(query)) {
        return true;
      }
      // Search in message content
      return chat.conversation.some(msg =>
        typeof msg.message === 'string' && msg.message.toLowerCase().includes(query)
      );
    });
  }, [chats, searchQuery]);

  const getActiveChat = () => {
    return chats.find(chat => chat.id === activeChatId);
  };

  const activeChat = getActiveChat();
  const isCompactLayout = Boolean(isSidebarMode);
  const chatMarkdownComponents = useMemo(() => getChatMarkdownComponents(isCompactLayout), [isCompactLayout]);

  const renderedConversation = useMemo(() => {
    const conv = activeChat?.conversation || [];
    const out = [];
    for (const msg of conv) {
      const rawText = typeof msg?.message === 'string' ? msg.message : String(msg?.message || '');
      const text = isAssistantMessage(msg)
        ? normalizeMarkdownForDisplay(stripAssistantDisclaimer(rawText))
        : rawText;
      // Keep user turns visible even when content is unexpectedly empty in persisted history.
      if (msg?.type !== 'user' && text.trim().length === 0) {
        continue;
      }

      const prev = out[out.length - 1];
      if (prev && isAssistantMessage(prev) && isAssistantMessage(msg)) {
        const prevNorm = normalizeAssistantTextForDedupe(prev.message);
        const currNorm = normalizeAssistantTextForDedupe(text);
        if (prevNorm && currNorm && prevNorm === currNorm) {
          continue; // Suppress adjacent duplicated assistant bubble.
        }
      }
      out.push({
        ...msg,
        message: text,
      });
    }
    return out;
  }, [activeChat]);

  // Helper function to get filtered message count (excluding empty messages)
  const getFilteredMessageCount = (conversation) => {
    if (!conversation || conversation.length === 0) return 0;

    return conversation
      .filter((msg) => {
        const content = (msg?.message || '').trim();
        return content.length > 0;
      }).length;
  };

  // User's frequently asked questions: derived from their chat history (logged-in only)
  const userFrequentQueries = useMemo(() => {
    if (!chats || chats.length === 0) return [];
    const countByNormalized = new Map(); // normalizedKey -> { count, example }
    for (const chat of chats) {
      const conv = chat?.conversation;
      if (!conv || !Array.isArray(conv)) continue;
      for (const msg of conv) {
        if (msg?.type !== 'user') continue;
        const raw = typeof msg.message === 'string' ? msg.message : '';
        const text = raw.trim();
        if (text.length === 0) continue;
        const key = normalizeQuery(text);
        if (!key) continue;
        const existing = countByNormalized.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          countByNormalized.set(key, { count: 1, example: text });
        }
      }
    }
    return Array.from(countByNormalized.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_USER_FAQ_SUGGESTIONS)
      .map(({ example }) => example);
  }, [chats]);

  // Removed localStorage polling (unused state)

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Show relative time for recent messages
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      // Show full date for older messages
      return date.toLocaleDateString('en-AU', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'Australia/Brisbane'
      });
    } catch (e) {
      return null;
    }
  };

  // Scroll to bottom - only when loading state changes (message being sent/received)
  const prevLoadingRef = useRef(loading);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Scroll when loading transitions from true to false (response received)
    if (prevLoadingRef.current === true && loading === false) {
      scrollToBottom();

      // Increment usage count
      const newUsageCount = usageCount + 1;
      setUsageCount(newUsageCount);
      localStorage.setItem('chat_usage_count', newUsageCount.toString());

      // Check for random prompt after a short delay
      setTimeout(() => {
        // Only show if we have a valid last message to attach feedback to
        const lastMsg = activeChat?.conversation[activeChat.conversation.length - 1];
        if (lastMsg && lastMsg.type === 'ai' && shouldShowFeedbackPrompt()) {
          setActiveFeedbackMsgId(lastMsg.id); // Set the target message ID logic
          setShowRandomFeedbackPrompt(true);
          setLastFeedbackPrompt(Date.now());
          localStorage.setItem('chat_last_feedback_prompt', Date.now().toString());
        }
      }, 3000);
    }

    prevLoadingRef.current = loading;
  }, [loading, usageCount, shouldShowFeedbackPrompt, activeChat]);

  // Also scroll when conversation changes (new messages added)
  useEffect(() => {
    if (activeChat && activeChat.conversation.length > 0) {
      scrollToBottom();
    }
  }, [activeChat, activeChat?.conversation.length]);


  // Removed unused sidebar resize handler
  // const handleMouseDown = (e) => {
  //   e.preventDefault();
  //   setIsResizing(true);
  // };

  // Sidebar resize logic removed (was unused - handleMouseDown not attached to UI)
  // useEffect(() => {
  //   const handleMouseMove = (e) => {
  //     if (!isResizing) return;
  //     const newWidth = e.clientX;
  //     if (newWidth > 200 && newWidth < 500) {
  //       setSidebarWidth(newWidth);
  //     }
  //   };
  //   const handleMouseUp = () => setIsResizing(false);
  //   if (isResizing) {
  //     document.addEventListener('mousemove', handleMouseMove);
  //     document.addEventListener('mouseup', handleMouseUp);
  //   }
  //   return () => {
  //     document.removeEventListener('mousemove', handleMouseMove);
  //     document.removeEventListener('mouseup', handleMouseUp);
  //   };
  // }, [isResizing]);

  return (
    <div className={isSidebarMode ? "chat-manager h-full flex flex-col bg-white" : "chat-manager lg:col-span-3 h-[900px] flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"}>

      {/* Mobile Sidebar Overlay */}
      {showMobileSidebar && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}

      {/* Main Layout Container */}
      <div className="flex-1 flex overflow-hidden relative">

        {/* Chat List Sidebar (Hidden - Now using dropdown menu) */}
        <div
          className="hidden"
        >
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <h3 className="font-semibold text-gray-700">Chat History</h3>
            <button onClick={() => setShowMobileSidebar(false)} className="lg:hidden p-1 hover:bg-gray-200 rounded-md">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* New Chat Button */}
          <div className="p-3">
            <button
              onClick={createNewChat}
              className="w-full flex items-center justify-center space-x-2 py-2.5 px-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">New Thread</span>
            </button>
          </div>

          {/* Search */}
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 bg-gray-50 border-none rounded-lg text-sm text-gray-700 focus:ring-1 focus:ring-gray-200 placeholder-gray-400"
              />
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
            {chatsLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : filteredChats.length === 0 ? (
              <div className="text-center py-8 text-xs text-gray-400">No chats found</div>
            ) : (
              filteredChats.map(chat => (
                <button
                  key={chat.id}
                  onClick={() => { switchChat(chat.id); setShowMobileSidebar(false); }}
                  className={`w-full text-left p-3 rounded-lg transition-all border border-transparent group
                      ${chat.isActive
                      ? 'bg-blue-50 border-blue-100 shadow-sm'
                      : 'hover:bg-gray-50 text-gray-600'
                    }
                    `}
                >
                  <div className="flex justify-between items-start mb-0.5">
                    <span className={`text-sm font-medium line-clamp-1 ${chat.isActive ? 'text-blue-700' : 'text-gray-700'}`}>
                      {searchQuery ? highlightMatch(chat.title, searchQuery) : chat.title}
                    </span>
                    {chats.length > 1 && (
                      <div
                        onClick={(e) => { e.stopPropagation(); deleteChat(chat.id); }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500 transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center text-[10px] text-gray-400">
                    <span>{getFilteredMessageCount(chat.conversation)} msgs</span>
                    <span>{formatTimestamp(chat.updatedAt || chat.createdAt)}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area - min-h-0 so only messages scroll, header stays visible */}
        <div className="chat-main-area flex-1 flex flex-col min-w-0 min-h-0 bg-white">

          {/* Modern Header - flex-shrink-0 so never clipped */}
          <div className="chat-main-header flex-shrink-0 h-14 border-b border-gray-100 flex items-center justify-between px-4 bg-white/80 backdrop-blur-sm z-10 w-full">
            <div className="flex items-center space-x-3">
              {!isSidebarMode && (
                <button
                  onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                  className="flex items-center justify-center min-h-[44px] min-w-[44px] p-2 hover:bg-gray-100 rounded-lg lg:hidden"
                  aria-label="Open chat menu"
                >
                  <Menu className="w-5 h-5 text-gray-600" />
                </button>
              )}

              <div className="flex items-center relative">
                <button
                  onClick={() => setShowHistoryMenu(!showHistoryMenu)}
                  className="flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-w-0 hover:opacity-80 transition-opacity rounded-lg hover:bg-gray-50"
                  title="Chat History & Options"
                >
                  <div className="chat-header-avatar w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-sm mr-3 flex-shrink-0">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="chat-header-title text-sm font-semibold text-gray-800">Maintenance AI</h2>
                    <div className="flex items-center space-x-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="chat-header-status text-[10px] uppercase tracking-wider font-medium text-gray-400">Online</span>
                    </div>
                  </div>
                </button>

                {/* History Dropdown Menu */}
                {showHistoryMenu && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowHistoryMenu(false)}
                    />

                    {/* Dropdown */}
                    <div className="chat-history-menu absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[70vh] flex flex-col animate-scale-up">
                      {/* Header */}
                      <div className="chat-history-header p-4 border-b border-gray-100 bg-gray-50/50">
                        <h3 className="font-semibold text-gray-700 text-sm">Chat History</h3>
                      </div>

                      {/* New Thread Button */}
                      <div className="chat-history-new-thread-wrap p-3 border-b border-gray-100">
                        <button
                          onClick={() => {
                            createNewChat();
                            setShowHistoryMenu(false);
                          }}
                          className="chat-history-new-thread-btn w-full flex items-center justify-center space-x-2 py-2.5 px-4 rounded-lg transition-colors shadow-sm"
                        >
                          <Plus className="w-4 h-4" />
                          <span className="text-sm font-medium">New Thread</span>
                        </button>
                      </div>

                      {/* Search */}
                      <div className="chat-history-search-wrap px-3 py-2 border-b border-gray-100">
                        <div className="relative">
                          <Search className="chat-history-search-icon absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search chats..."
                            className="chat-history-search-input w-full pl-9 pr-3 py-2 bg-gray-50 border-none rounded-lg text-sm text-gray-700 focus:ring-1 focus:ring-gray-200 placeholder-gray-400"
                          />
                        </div>
                      </div>

                      {/* Chat List */}
                      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
                        {chatsLoading ? (
                          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                        ) : filteredChats.length === 0 ? (
                          <div className="text-center py-8 text-xs text-gray-400">No chats found</div>
                        ) : (
                          filteredChats.map(chat => (
                            <button
                              key={chat.id}
                              onClick={() => {
                                switchChat(chat.id);
                                setShowHistoryMenu(false);
                              }}
                              className={`chat-history-item w-full text-left p-3 rounded-lg transition-all border border-transparent group
                                ${chat.isActive
                                  ? 'chat-history-item-active bg-blue-50 border-blue-100 shadow-sm'
                                  : 'hover:bg-gray-50 text-gray-600'
                                }
                              `}
                            >
                              <div className="flex justify-between items-start mb-0.5">
                                <span className={`text-sm font-medium line-clamp-1 ${chat.isActive ? 'text-blue-700' : 'text-gray-700'}`}>
                                  {searchQuery ? highlightMatch(chat.title, searchQuery) : chat.title}
                                </span>
                                {chats.length > 1 && (
                                  <div
                                    onClick={(e) => { e.stopPropagation(); deleteChat(chat.id); }}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500 transition-opacity"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </div>
                                )}
                              </div>
                              <div className="flex justify-between items-center text-[10px] text-gray-400">
                                <span>{getFilteredMessageCount(chat.conversation)} msgs</span>
                                <span>{formatTimestamp(chat.updatedAt || chat.createdAt)}</span>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-1">
              {activeChat && getFilteredMessageCount(activeChat.conversation) > 0 && (
                <button
                  onClick={clearConversation}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Clear conversation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Messages Area - min-h-0 so it gets remaining height and scrolls */}
          <div className={`chat-messages flex-1 min-h-0 overflow-y-auto ${isCompactLayout ? 'p-3 space-y-4' : 'p-4 space-y-6'} bg-gray-50/30 scroll-smooth`}>
            {!activeChat || activeChat.conversation.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-4 animate-fade-in-up">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                  <Bot className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">How can I help you today?</h3>
                <p className="text-sm text-gray-500 max-w-xs mb-8">
                  {isAuthenticated
                    ? (userFrequentQueries.length > 0
                      ? "Questions you ask often."
                      : "Ask about fleet status, analyze maintenance events, or predict future failures.")
                    : 'Ask about fleet status, analyze maintenance events, or predict future failures.'}
                </p>

                {/* Prompt Starters: preset for guests; user's frequent queries for logged-in (fallback when no history) */}
                <div className="grid gap-2 w-full max-w-md">
                  {(isAuthenticated
                    ? (userFrequentQueries.length > 0 ? userFrequentQueries : LOGGED_IN_FALLBACK_QUERIES)
                    : PRESET_QUERIES).map((text, i) => (
                    <button
                      key={i}
                      onClick={() => setQuery(text)}
                      className="chat-starter-btn text-sm text-gray-600 bg-white border border-gray-200 p-3 rounded-xl hover:border-blue-300 hover:shadow-sm hover:text-blue-600 transition-all text-left"
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className={`${isCompactLayout ? 'space-y-4 pb-3' : 'space-y-6 pb-4'}`}>
                {renderedConversation
                  .map((msg, index) => (
                    <div key={msg.id || index} className={`group flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`${isCompactLayout ? `max-w-[86%] lg:${msg.type === 'user' ? 'max-w-[70%]' : 'max-w-[76%]'}` : `max-w-[88%] lg:${msg.type === 'user' ? 'max-w-[75%]' : 'max-w-[82%]'}`} min-w-0 ${msg.type === 'user' ? 'order-1' : 'order-2'}`}>

                        {/* Attachment */}
                        {msg.attachment && (
                          <div className="mb-1 flex justify-end">
                            <div className="bg-gray-100 text-xs px-3 py-1 rounded-lg flex items-center space-x-1 border border-gray-200">
                              <Paperclip className="w-3 h-3 text-gray-400" />
                              <span>{msg.attachment.name}</span>
                            </div>
                          </div>
                        )}

                        {/* Bubble */}
                        <div className={`
                                  chat-bubble ${msg.type === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}
                                  relative ${isCompactLayout ? 'px-3.5 sm:px-4 py-2.5 rounded-xl text-[13px] leading-6' : 'px-4 sm:px-5 py-3.5 rounded-2xl text-sm leading-relaxed'} shadow-sm break-words
                                  ${msg.type === 'user'
                            ? 'text-white rounded-br-none'
                            : 'border border-gray-100 text-gray-700 rounded-bl-none'
                          }
                               `}>
                          {typeof msg.message === 'string' ? (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={chatMarkdownComponents}
                              className={`chat-markdown ${isCompactLayout ? 'chat-markdown-compact' : ''} ${msg.type === 'user' ? 'chat-markdown-user' : 'chat-markdown-assistant'} break-words`}
                            >
                              {msg.message.trim().length > 0 ? msg.message : (msg.type === 'user' ? '_[message unavailable]_' : '')}
                            </ReactMarkdown>
                          ) : (
                            <pre className="text-xs overflow-x-auto">{JSON.stringify(msg.message, null, 2)}</pre>
                          )}
                        </div>

                        {/* Metadata / Actions */}
                        {msg.type === 'ai' && (
                          <div className="mt-2 flex items-center space-x-2">
                            {/* Feedback Buttons - Show on hover or if feedback given */}
                            <div className={`flex items-center space-x-1 ml-auto ${messageFeedback[msg.id] ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                              <button
                                onClick={() => handleFeedback(msg.id, true)}
                                className={`p-1.5 rounded-lg transition-all ${messageFeedback[msg.id] === 'up'
                                  ? 'bg-green-50 text-green-600'
                                  : 'text-gray-400 hover:bg-gray-100 hover:text-green-500'
                                  }`}
                                title="Helpful response"
                              >
                                <ThumbsUp className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleFeedback(msg.id, false)}
                                className={`p-1.5 rounded-lg transition-all ${messageFeedback[msg.id] === 'down'
                                  ? 'bg-red-50 text-red-600'
                                  : 'text-gray-400 hover:bg-gray-100 hover:text-red-500'
                                  }`}
                                title="Not helpful"
                              >
                                <ThumbsDown className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => {
                                  setActiveFeedbackMsgId(msg.id);
                                  setShowCommentModal(true);
                                }}
                                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-blue-500 transition-all"
                                title="Add comment"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  ))
                }
                {loading && (
                  <div className="flex justify-start animate-in fade-in slide-in-from-left-2 transition-all">
                    <div className="chat-bubble chat-bubble-ai border border-gray-100 px-4 py-3 rounded-2xl rounded-bl-none shadow-sm flex flex-col space-y-2 min-w-[200px] max-w-[85%]">
                      <div className="flex items-center space-x-3">
                        <div className="flex space-x-1 shrink-0">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                        <span className="text-xs font-medium text-blue-600 animate-pulse truncate">
                          {processingStatus?.message || 'Analysing request...'}
                        </span>
                      </div>

                      {/* Optional Progress Bar for complex steps */}
                      {processingStatus?.step && (
                        <div className="w-full bg-gray-50 h-1 rounded-full overflow-hidden">
                          <div
                            className="bg-blue-400 h-full transition-all duration-700 ease-in-out"
                            style={{
                              width: `${processingStatus.step === 99 ? 100 : Math.max(15, (processingStatus.step / 10) * 100)}%`
                            }}
                          ></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area - flex-shrink-0 so always visible */}
          <div className="chat-input-area flex-shrink-0 p-4 bg-white border-t border-gray-100">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (selectedFile && handleFileUpload) {
                  await handleFileUpload(selectedFile, query);
                  setSelectedFile(null);
                  setQuery('');
                } else {
                  handleSubmit(e);
                }
              }}
              className="relative"
            >
              {selectedFile && (
                <div className="absolute bottom-full mb-2 left-0 w-full bg-blue-50 border border-blue-100 p-2 rounded-lg flex items-center justify-between text-sm text-blue-800">
                  <span className="flex items-center truncate">
                    <FileText className="w-4 h-4 mr-2" />
                    {selectedFile.name}
                  </span>
                  <button type="button" onClick={() => setSelectedFile(null)}><X className="w-4 h-4" /></button>
                </div>
              )}

              <div
              className="chat-input-shell flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-2xl p-2 transition-all shadow-sm focus-within:border-gray-300"
                data-tour-id="chat-input"
              >
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-xl transition-colors shrink-0"
                  title="Upload File"
                >
                  <Paperclip className="w-5 h-5" />
                </button>

                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (query.trim() || selectedFile) {
                        if (selectedFile && handleFileUpload) {
                          handleFileUpload(selectedFile, query).then(() => {
                            setSelectedFile(null);
                            setQuery('');
                          });
                        } else {
                          handleSubmit(e);
                        }
                      }
                    }
                  }}
                  placeholder="Type your message..."
                  className="chat-input-text flex-1 bg-transparent border-none focus:ring-0 focus:outline-none text-gray-800 placeholder-gray-400 resize-none max-h-32 py-2 text-sm leading-relaxed"
                  rows={1}
                  style={{ minHeight: '40px' }}
                />

                {loading ? (
                  <button type="button" onClick={cancelRequest} className="p-2 bg-red-50 text-red-500 rounded-xl hover:bg-red-100">
                    <X className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!query.trim() && !selectedFile}
                    className="chat-send-btn p-2 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all active:scale-95"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="mt-2 px-1 text-xs text-gray-500">
                AI responses may be inaccurate. Please verify critical information.
              </p>
              <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => {
                if (e.target.files?.[0]) setSelectedFile(e.target.files[0]);
                e.target.value = '';
              }} />
            </form>
          </div>
        </div>

        {/* Comment Modal */}
        {showCommentModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-bold text-gray-900">Provide Feedback</h3>
                <button
                  onClick={() => setShowCommentModal(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  disabled={isSubmittingFeedback}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-600 mb-4">
                  Help us improve the chatbot by sharing your thoughts or reporting improper results.
                </p>
                <textarea
                  className="w-full h-32 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm resize-none"
                  placeholder="Describe the issue or suggestion..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  autoFocus
                  disabled={isSubmittingFeedback}
                />
              </div>
              <div className="p-5 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => setShowCommentModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={isSubmittingFeedback}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCommentSubmit}
                  disabled={!commentText.trim() || isSubmittingFeedback}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmittingFeedback ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : 'Submit Feedback'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Random Feedback Prompt (Bottom Right Notification) */}
        {showRandomFeedbackPrompt && !showCommentModal && (
          <div className="fixed bottom-5 right-5 z-50 animate-in slide-in-from-bottom-5 fade-in duration-400">
            <div className="w-[330px] max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200/80 bg-white/95 shadow-[0_16px_40px_-18px_rgba(15,23,42,0.45)] backdrop-blur-sm overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3.5 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200/80 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-xl mr-3 ring-1 ring-blue-200/70">
                    <MessageSquare className="w-4 h-4 text-blue-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm leading-none">Quick Feedback</h4>
                    <p className="text-xs text-slate-500 mt-1">How helpful was this response?</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRandomFeedbackPrompt(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-white/90 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Quick Actions */}
              <div className="p-4 space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (activeFeedbackMsgId) handleFeedback(activeFeedbackMsgId, true);
                      setShowRandomFeedbackPrompt(false);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition-all font-medium text-sm"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                    Helpful
                  </button>
                  <button
                    onClick={() => {
                      if (activeFeedbackMsgId) handleFeedback(activeFeedbackMsgId, false);
                      setShowRandomFeedbackPrompt(false);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-300 transition-all font-medium text-sm"
                  >
                    <ThumbsDown className="w-3.5 h-3.5" />
                    Not Helpful
                  </button>
                </div>

                <button
                  onClick={() => {
                    setShowRandomFeedbackPrompt(false);
                    setShowCommentModal(true); // activeFeedbackMsgId is already set
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors text-sm font-medium"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Add Comment
                </button>
              </div>

              {/* Footer */}
              <div className="px-4 pb-3.5">
                <p className="text-[11px] text-slate-400 text-center">
                  Your feedback helps improve response quality.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatManager;
